import { useState, useEffect } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { setDelogueApiToken } from 'lib/api/utils';
import { type AuthContext, AuthTokenContext } from 'features/auth/hooks/authContext';

const AuthTokenProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [authContext, setAuthContext] = useState<AuthContext>({
    isLoading: false,
    isAuthenticated: false,
    token: null,
  });

  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      setDelogueApiToken(null);
      setAuthContext({ isLoading, isAuthenticated, token: null });
      return;
    }
    const fetchToken = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        setDelogueApiToken(accessToken);
        setAuthContext({ isLoading, isAuthenticated, token: accessToken });
      } catch {
        setDelogueApiToken(null);
        setAuthContext({ isLoading: false, isAuthenticated: false, token: null });
      }
    };
    fetchToken();
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  return <AuthTokenContext.Provider value={authContext}>{children}</AuthTokenContext.Provider>;
};

type AuthProviderProps = {
  children: React.ReactNode;
};

export const AuthProviderFederated = ({ children }: AuthProviderProps) => {
  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      }}
    >
      <AuthTokenProvider>{children}</AuthTokenProvider>
    </Auth0Provider>
  );
};
